<template lang="pug">
//- Desktop = permanent drawer with rail collapse.  Mobile = temporary
//- drawer bound to the parent's `mobileOpen` model, so App.vue's
//- hamburger button controls visibility.  Rail mode is disabled on
//- mobile because the mini-icon column would just eat map space —
//- users open the drawer, tap, drawer dismisses.
v-navigation-drawer(
    v-if='!fullscreen'
    :model-value='isMobile ? mobileOpen : true'
    :permanent='!isMobile'
    :temporary='isMobile'
    app
    dark
    width='220'
    :rail='!isMobile && rail'
    @update:model-value='(v) => isMobile && $emit("update:mobileOpen", v)'
)
    //- Collapse chevron hidden on mobile — the drawer is temporary
    //- there, tapping outside dismisses, no rail state to twiddle.
    v-list-item(
        v-if='!isMobile'
        :prepend-icon='rail ? "mdi-chevron-right" : "mdi-chevron-left"'
        :title='rail ? "" : "Collapse"'
        density='compact'
        min-height='40'
        style='cursor: pointer;'
        @click='rail = !rail'
    )
    v-divider
    //- Single outer v-list so every top-level menu item shares the same
    //- list container — one BaseListWrapper per item was giving every row
    //- its own v-list with its own padding-block, producing visible gaps.
    v-list(density='compact' variant='flat' lines='one' select-strategy='single-leaf' class='py-0')
        template(v-for='menuItem in menuItems' :key='menuItem.routeName ?? menuItem.text')
            div(v-if='menuItem.featureDisabled !== true')
                template(v-if='menuItem.children')
                    v-list-group
                        template(v-slot:activator='{ props: groupProps }')
                            v-tooltip(:text='menuItem.text' location='end' :disabled='!rail')
                                template(v-slot:activator='{ props: tipProps }')
                                    v-list-item(v-bind='{ ...groupProps, ...tipProps }' :prepend-icon='menuItem.icon' :title='menuItem.text' density='compact' min-height='32')
                        v-tooltip(
                            v-for='child in menuItem.children'
                            :key='child.routeName'
                            :text='child.text'
                            location='end'
                            :disabled='!rail'
                        )
                            template(v-slot:activator='{ props: tipProps }')
                                v-list-item(
                                    v-bind='tipProps'
                                    :to='resolveMenuTo(child.to)'
                                    :prepend-icon='child.icon'
                                    :title='child.text'
                                    density='compact'
                                    min-height='32'
                                )
                template(v-else)
                    v-tooltip(:text='menuItem.text' location='end' :disabled='!rail')
                        template(v-slot:activator='{ props: tipProps }')
                            v-list-item(
                                v-bind='tipProps'
                                link
                                :to='resolveMenuTo(menuItem.to)'
                                :target='menuItem?.target ? menuItem.target : ""'
                                :prepend-icon='menuItem.icon'
                                :title='menuItem.text'
                                density='compact'
                                min-height='32'
                            )
    template(v-slot:append='')
        div(v-show='!rail' style='padding: 8px 16px;')
            v-select(
                :model-value='operationStore.currentOperationId'
                :items='workspaceItems'
                item-title='name'
                item-value='id'
                density='compact'
                variant='outlined'
                hide-details
                label='Operation'
                @update:model-value='switchWorkspace'
            )
                template(v-slot:item='{ item, props: itemProps }')
                    v-list-item(v-bind='itemProps' :subtitle='workspaceSubtitle(item.raw)')
                        template(v-if='item.raw?.isTutorial' v-slot:append)
                            //- Tutorial rows come in two flavours: a synthetic "not started"
                            //- preview (no ownerSub) that will materialise on first
                            //- access, and an already-materialised per-user instance
                            //- (ownerSub set) that the user can reset back to seed.
                            v-chip(v-if='!item.raw?.ownerSub' size='x-small' color='info' variant='tonal') Tutorial
                            template(v-else)
                                v-chip(size='x-small' color='success' variant='tonal') Yours
                                v-btn(
                                    icon='mdi-restart'
                                    size='x-small'
                                    variant='text'
                                    color='warning'
                                    class='ml-1'
                                    title='Reset this tutorial to its starting state (discards your changes)'
                                    @click.stop='onResetTutorial(item.raw)'
                                )
        v-btn(block color='red' prepend-icon='mdi-logout' @click='logout' size='small') {{ rail ? '' : 'Logout' }}
</template>

<script lang="ts">
import { defineComponent, computed, ref, onMounted, onBeforeUnmount } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useOperationStore } from '@/store/storeOperation';
import tacterisLogo from '@/assets/tacteris-logo.white.svg';

export default defineComponent({
    name: 'BaseNavigationMenu',
    props: {
        menuItems: {
            type: Array,
            required: true,
        },
        // Mobile-only: parent's drawer-open state.  Reflected into the
        // v-navigation-drawer's v-model so App.vue's hamburger can
        // toggle it and outside-taps can close it via
        // update:mobileOpen.  Ignored when isMobile is false.
        mobileOpen: {
            type: Boolean,
            default: false,
        },
        isMobile: {
            type: Boolean,
            default: false,
        },
    },
    emits: ['update:mobileOpen'],
    setup() {
        const router = useRouter();
        const route = useRoute();
        const operationStore = useOperationStore();

        const workspaceItems = computed(() => operationStore.operations);

        const rail = ref(true);
        const fullscreen = ref(false);
        const onFullscreenChange = () => {
            fullscreen.value = !!document.fullscreenElement;
        };
        onMounted(() => document.addEventListener('fullscreenchange', onFullscreenChange));
        onBeforeUnmount(() => document.removeEventListener('fullscreenchange', onFullscreenChange));

        const logout = () => {
            router.push('/logout');
        };

        const switchWorkspace = (newWorkspaceId: string) => {
            operationStore.setCurrentOperation(newWorkspaceId);
            // Navigate to the same named route but in the new operation
            const currentRouteName = route.name;
            if (currentRouteName) {
                router.push({ name: currentRouteName as string, params: { operationId: newWorkspaceId } });
            } else {
                router.push(`/operation/${newWorkspaceId}`);
            }
        };

        // Inject operationId param into named route `to` objects so
        // they work under /operation/:operationId/ prefix
        const resolveMenuTo = (to: any) => {
            if (to && typeof to === 'object' && to.name && operationStore.currentOperationId) {
                return { ...to, params: { ...to.params, operationId: operationStore.currentOperationId } };
            }
            return to;
        };

        // Second line under each operation name in the picker.  Tutorial
        // instances get a "your private copy" hint so operators aren't
        // surprised that their edits are isolated from other users.
        const workspaceSubtitle = (w: any): string | undefined => {
            if (!w) return undefined;
            if (w.isTutorial && w.ownerSub) return w.description
                ? `${w.description} — your private copy`
                : 'Your private copy — resettable';
            if (w.isTutorial) return w.description ?? 'Fresh copy will open on first access';
            return w.description ?? undefined;
        };

        const onResetTutorial = async (w: any) => {
            if (!w?.id) return;
            const ok = window.confirm(
                `Reset "${w.name}"?\n\nThis discards every change you've made in this tutorial operation `
                + 'and starts you over from the seed content.  Other users are unaffected.'
            );
            if (!ok) return;
            try {
                await operationStore.resetTutorialOperation(w.id);
            } catch (err) {
                console.error('[Navigation] Failed to reset tutorial operation:', err);
                alert('Failed to reset the tutorial operation. See console for details.');
            }
        };

        return {
            logout,
            rail,
            fullscreen,
            tacterisLogo,
            operationStore,
            workspaceItems,
            switchWorkspace,
            resolveMenuTo,
            workspaceSubtitle,
            onResetTutorial,
        };
    },
});
</script>
<style lang="scss" scoped>
.wrapMenuTitle {
    white-space: normal !important;
}
</style>
